import { logError } from 'fergy-core-react-logging';
import React, { type FunctionComponent } from 'react';
import {
	DEFAULT_PRODUCT_SORT,
	SEARCH_HEADER_ELEMENT,
	SEARCH_RESULT_LIMIT,
	SEARCH_SORT_OPTIONS,
	SIMPLIFIED_SEARCH_SORT_OPTIONS,
	SIMPLIFIED_STOCK_STATUS_OPTIONS,
	STOCK_STATUS_OPTIONS
} from '../../../constants/search';
import { booleanFromQueryString, formatNumber } from '../../../helpers/general-helper/general-helper';
import { isRangeFacetGroup } from '../../../helpers/search-helper/search-helper';
import { convertToProductSortEnum } from '../../../helpers/search-helper/search-product-helper';
import { useProductViewPreference, useSearchResults } from '../../../hooks/apollo/search/search.hooks';
import { useFeatures } from '../../../hooks/features/features.hooks';
import { StyledButton } from '../../buttons';
import { Badge } from '../../common-components/badge/badge.component';
import { DropdownMenu, DropdownMenuItem } from '../../common-components/dropdown-menu/dropdown-menu.component';
import { SelectBox, SelectBoxWithInsetLabel, type SelectOptionProps } from '../../inputs/select-box/select-box.component';
import ArrowDropDownIcon from '../../svgs/arrow-drop-down.svg';
import DotPointsIcon from '../../svgs/untitled-ui/dotpoints.svg';
import FilterLinesIcon from '../../svgs/untitled-ui/filter-lines.svg';
import GridViewIcon from '../../svgs/untitled-ui/grid-layout-2x2.svg';

export type SearchHeaderProps = {
	title?: string;
	sortOptions?: SelectOptionProps[];
	useCategorySort?: boolean;
	showNonstock?: boolean;
	onShowDrawer: () => void;
};
// TODO: EFDC-33203 - refactor to reduce cognitive complexity
export const SearchHeader: FunctionComponent<SearchHeaderProps> = ({
	title,
	onShowDrawer,
	showNonstock,
	sortOptions = SEARCH_SORT_OPTIONS
	// eslint-disable-next-line sonarjs/cognitive-complexity
}) => {
	const { results, loading, query, sortBy, setSortBy, isNonstock, setNonstock } = useSearchResults();
	const { productViewPreference, setProductViewPreference } = useProductViewPreference();
	const toggleView = () => {
		setProductViewPreference(productViewPreference === 'TILE' ? 'LIST' : 'TILE').catch(logError);
	};
	const productViewOptions = [
		{ name: 'List', value: 'LIST' },
		{ name: 'Grid', value: 'GRID' }
	];
	const currentViewOptionIndex = () => {
		return productViewPreference === 'TILE' ? 1 : 0;
	};
	const count = results?.count || 0;

	let resultCount = formatNumber(Math.min(count, SEARCH_RESULT_LIMIT));
	if (count > SEARCH_RESULT_LIMIT) {
		resultCount = `${resultCount}+`;
	}
	const [isSortInsideFacetDrawer, isSearchDropdownRestyle] = useFeatures(['sort-inside-facet-drawer', 'search-dropdown-restyle']);

	if (isSearchDropdownRestyle) {
		sortOptions = SIMPLIFIED_SEARCH_SORT_OPTIONS;
	}
	const stockOptions = isSearchDropdownRestyle ? SIMPLIFIED_STOCK_STATUS_OPTIONS : STOCK_STATUS_OPTIONS;
	const selectedFacetCounts =
		results?.selectedFacetGroups?.reduce((facetCount, group) => {
			const isRangeGroup = isRangeFacetGroup(group);
			return facetCount + (isRangeGroup ? 1 : group.facets?.length ?? 0);
		}, 0) ?? 0;

	const sortChanged = (event: React.ChangeEvent<HTMLSelectElement>) => {
		const sort = convertToProductSortEnum(event.target.value);
		setSortBy(sort);
	};
	const sortChangedV2 = (selectedIndex: number) => {
		const sort = convertToProductSortEnum(SIMPLIFIED_SEARCH_SORT_OPTIONS[selectedIndex].value);
		setSortBy(sort);
	};
	const currentSortIndex = () => sortOptions.findIndex((option) => option.value === sortBy);
	const filterClick = () => {
		onShowDrawer();
	};

	const stockStatusChanged = (event: React.ChangeEvent<HTMLSelectElement>) => {
		const nonstock = booleanFromQueryString(event.target.value);
		setNonstock(nonstock);
	};
	const stockStatusChangedV2 = (index: number) => {
		setNonstock(stockOptions[index].value === 'true');
	};

	const facetCountText = selectedFacetCounts > 0 ? `(${selectedFacetCounts})` : '';

	const hasSortBy = sortBy !== DEFAULT_PRODUCT_SORT;
	const facetAndSortCounts = hasSortBy ? selectedFacetCounts + 1 : selectedFacetCounts;
	const facetAndSortCountText = facetAndSortCounts > 0 ? `(${facetAndSortCounts})` : '';

	const displayedFacetCount = isSortInsideFacetDrawer ? facetAndSortCountText : facetCountText;
	const sortSelectClassName = isSortInsideFacetDrawer ? 'dn db-ns' : '';

	const renderViewOption = (option: string) => (
		<div className="flex items-center">
			{option === 'List' ? <DotPointsIcon data-testid="DotPointsIcon" /> : <GridViewIcon />}
			<div className=" ml2">{option}</div>
		</div>
	);

	return (
		<div id={SEARCH_HEADER_ELEMENT} className="flex flex-wrap justify-between-ns">
			<div className="w-100 w-auto-ns">
				{results?.correctedSearchTerm && (
					<div className="ma3">
						We didn't find any products for <span className="theme-error">{query}</span>. Showing results for...
					</div>
				)}
				<h1 className="dib ma0" data-automation="search-header-text">
					{title}
				</h1>
				<div className={`mt1 mt0-l mb1 mb0-l ml2-l dib-l ${loading ? 'o-0' : ''}`} data-automation="results-count">
					{resultCount} Products
				</div>
			</div>
			<div className="flex flex-wrap">
				{showNonstock && (
					<span className={`mr2 ${isSortInsideFacetDrawer ? 'flex db-ns' : 'mb1'}`}>
						{isSearchDropdownRestyle ? (
							<div className="mr2">
								<DropdownMenu
									title={stockOptions[isNonstock ? 1 : 0].name}
									dropdownClassName="pv2"
									ariaLabel="stock status"
									theme="INTERNAL"
									automationHook="product-stock-status"
									label="Nonstock"
									insetLabel={true}
									onChooseItem={stockStatusChangedV2}
									initialSelectedIndex={isNonstock ? 1 : 0}
									shouldCloseOnChoice={true}
									borderlessItems={true}>
									{stockOptions.map(({ name }) => (
										<DropdownMenuItem key={`option-${name}`} withoutPadding={true}>
											<div className="pa3 theme-luxury:p-0!">{name}</div>
										</DropdownMenuItem>
									))}
								</DropdownMenu>
							</div>
						) : (
							<SelectBox
								name="stockSelect"
								value={String(isNonstock)}
								options={stockOptions}
								automationHook="product-stock-status"
								ariaLabel="stock status"
								onChange={stockStatusChanged}
								data-testid="stock-status-select"
								employee={true}
							/>
						)}
					</span>
				)}
				<span className={`mr2 mb1 relative ${sortSelectClassName}`} style={isSearchDropdownRestyle ? { width: 215 } : {}}>
					{isSearchDropdownRestyle ? (
						<>
							<div className="dn db-ns">
								<DropdownMenu
									title={sortOptions[currentSortIndex()].name}
									dropdownClassName="pv2"
									theme="PRIMARY"
									ariaLabel="sort"
									automationHook="product-sort-by"
									label="Sort"
									insetLabel={true}
									onChooseItem={sortChangedV2}
									initialSelectedIndex={currentSortIndex()}
									shouldCloseOnChoice={true}
									borderlessItems={true}>
									{sortOptions.map((o, index) => (
										<DropdownMenuItem key={index} withoutPadding={true}>
											<div className="pa3 theme-luxury:p-0!">{o.name}</div>
										</DropdownMenuItem>
									))}
								</DropdownMenu>
							</div>
							<div className="dn-ns">
								<SelectBoxWithInsetLabel
									label="Sort"
									name="sortSelect"
									value={sortBy}
									options={sortOptions}
									automationHook="product-sort-by"
									ariaLabel="sort"
									onChange={sortChanged}
									data-testid="sort-select"
									borderStyle="dark"
								/>
							</div>
						</>
					) : (
						<SelectBox
							name="sortSelect"
							value={sortBy}
							options={sortOptions}
							automationHook="product-sort-by"
							ariaLabel="sort"
							onChange={sortChanged}
							data-testid="sort-select"
							borderStyle="dark"
						/>
					)}
				</span>
				{isSortInsideFacetDrawer && (
					<span className={`dn-ns ${facetAndSortCounts > 0 ? 'mr3' : 'mr2'} ${showNonstock ? 'mt1 mt0-ns' : undefined}`}>
						<Badge count={facetAndSortCounts} noMargin>
							<StyledButton onClick={filterClick} buttonStyle="SECONDARY">
								<div className="flex items-center">
									<FilterLinesIcon className="f4" />
									<span className="mh2">{`Sort & Filter`}</span>
									<ArrowDropDownIcon className="f3" />
								</div>
							</StyledButton>
						</Badge>
					</span>
				)}
				<span className={`dn-l mr2 mb1 ${isSearchDropdownRestyle ? 'mt1' : ''} ${isSortInsideFacetDrawer ? 'dn db-m' : ''}`}>
					<StyledButton onClick={filterClick} buttonStyle="SECONDARY">
						<span>{`Filter ${displayedFacetCount}`}</span>
					</StyledButton>
				</span>
				<span
					className={`${isSortInsideFacetDrawer ? 'flex db-ns' : ''} ${
						showNonstock && isSortInsideFacetDrawer ? 'mt1 mt0-ns' : ''
					}`}>
					{isSearchDropdownRestyle ? (
						<>
							<div className="dn db-ns">
								<DropdownMenu
									title={renderViewOption(productViewOptions[currentViewOptionIndex()].name)}
									dropdownClassName="pv2"
									theme="PRIMARY"
									ariaLabel="View"
									automationHook={productViewPreference}
									label="View"
									insetLabel={true}
									onChooseItem={toggleView}
									initialSelectedIndex={currentViewOptionIndex()}
									shouldCloseOnChoice={true}
									borderlessItems={true}>
									{productViewOptions.map(({ name }, index) => (
										<DropdownMenuItem key={index} withoutPadding={true}>
											<div className="pa3 theme-luxury:p-0!">{renderViewOption(name)}</div>
										</DropdownMenuItem>
									))}
								</DropdownMenu>
							</div>
							<div className="dn-ns">
								<SelectBoxWithInsetLabel
									label="View"
									name="viewSelect"
									value={productViewOptions[currentViewOptionIndex()].value}
									options={productViewOptions}
									ariaLabel="view"
									onChange={toggleView}
									data-testid="view-select"
									borderStyle="dark"
								/>
							</div>
						</>
					) : (
						<StyledButton automationHook={productViewPreference} onClick={toggleView} buttonStyle="SECONDARY">
							<div className="flex items-center">
								<div className="flex items-center">
									{productViewPreference === 'LIST' ? <DotPointsIcon data-testid="DotPointsIcon" /> : <GridViewIcon />}
								</div>
								<span className="ml2">{productViewPreference === 'LIST' ? 'List' : 'Grid'} View</span>
							</div>
						</StyledButton>
					)}
				</span>
			</div>
		</div>
	);
};
